import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ShieldCheck, Ticket, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { getPricing, formatPrice, PLANS } from "@/lib/pricing";
import { CompareTable } from "@/components/CompareTable";
import { track } from "@/lib/analytics";

const BENEFITS = [
  "Komunitas Premium: diskusi langsung dengan expert finance CeKKeU",
  "Stress test kehilangan gaji suami",
  "Stress test kehilangan gaji istri",
  "Simulasi kenaikan cicilan",
  "Family Survival Runway",
  "Target dana darurat",
  "Batas pengeluaran aman",
  "Kerentanan utama",
  "Improvement Simulator",
  "Rule-based Action Plan",
  "Saved Family Resilience Report",
];

export default function Paywall() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { report, premium, subscription, refreshSubscription } = useApp();
  const { isAuthenticated } = useAuth();
  const pricing = getPricing();
  const [paying, setPaying] = useState(false);
  const [autoRenew, setAutoRenew] = useState(false);
  const [planId, setPlanId] = useState(() => {
    const p = searchParams.get("plan");
    return PLANS.some((x) => x.id === p) ? p : "sixmonth";
  });
  const [voucher, setVoucher] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const selectedPlan = PLANS.find((p) => p.id === planId) || PLANS[0];

  // Where to land after unlocking. The daily-journal CTA sends users straight
  // to Catat Transaksi; other entry points default to the resilience page.
  const nextPath = searchParams.get("next") || "/app/ketahanan";
  const PENDING_VOUCHER = "fcs_pending_voucher";
  const autoTried = useRef(false);
  const isTrial = subscription?.source === "trial";

  // After the user registers/logs in to use a voucher, auto-apply the code they
  // already typed (saved before the auth redirect) so they don't re-enter it.
  useEffect(() => {
    if (!isAuthenticated || premium || autoTried.current) return;
    let code = null;
    try { code = sessionStorage.getItem(PENDING_VOUCHER); } catch { /* ignore */ }
    if (code) {
      autoTried.current = true;
      try { sessionStorage.removeItem(PENDING_VOUCHER); } catch { /* ignore */ }
      setVoucher(code);
      doRedeem(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (premium && !isTrial) return <Navigate to={nextPath} replace />;

  const requireLogin = () => {
    toast("Masuk dulu untuk berlangganan.");
    navigate(`/login?next=${encodeURIComponent(`/paywall?next=${nextPath}`)}`);
  };

  // Single, unified payment path for ALL devices (no mobile/desktop branching,
  // no window.open popups). We NEVER build the Midtrans URL ourselves — the
  // backend passes redirect_url through as-is; we only append the documented
  // GoPay deeplink hint. Premium is granted server-side via the webhook.
  const subscribe = async () => {
    if (!isAuthenticated) return requireLogin();
    setPaying(true);
    try {
      track("premium_clicked");
      const data = await api.paymentCreateTransaction(planId);
      // If the request failed or no redirect_url came back, show an error and
      // do NOT redirect anywhere (prevents the "Snap Token ID: null" page).
      if (!data || !data.redirectUrl) {
        toast.error("Gagal membuat transaksi pembayaran. Silakan coba lagi.");
        setPaying(false);
        return;
      }
      // Redirect the whole tab to the Midtrans-hosted payment page (Snap will
      // show whatever channels the merchant has active — QRIS now, GoPay after
      // it is activated in the Midtrans dashboard). We pass redirect_url as-is.
      window.location.href = data.redirectUrl; // same tab; /payment/return handles the result
    } catch (e) {
      toast.error(e.message || "Tidak bisa memulai pembayaran.");
      setPaying(false);
    }
  };

  const doRedeem = async (code) => {
    const c = (code || "").trim();
    if (!c) return;
    setRedeeming(true);
    try {
      const res = await api.voucherRedeem(c);
      await refreshSubscription();
      if (res.active) {
        track("premium_unlocked");
        toast.success("Voucher berhasil! Premium aktif 30 hari.");
        navigate(nextPath);
      } else {
        toast.error("Voucher tidak dapat diproses saat ini. Coba lagi.");
      }
    } catch (e) {
      toast.error(e.message || "Kode voucher tidak valid.");
    } finally {
      setRedeeming(false);
    }
  };

  const redeem = () => {
    if (!voucher.trim()) return;
    if (!isAuthenticated) {
      // Remember the code so it auto-applies right after registration/login.
      try { sessionStorage.setItem(PENDING_VOUCHER, voucher.trim()); } catch { /* ignore */ }
      return requireLogin();
    }
    doRedeem(voucher);
  };

  return (
    <div className="mx-auto min-h-screen w-full max-w-md lg:max-w-3xl bg-[#f8f8f7] pb-40">
      <header className="flex items-center gap-3 px-6 pb-2 pt-6">
        <button onClick={() => navigate(-1)} data-testid="paywall-back" className="text-zinc-500">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <span className="font-heading text-xs font-semibold text-zinc-400">Langganan Premium</span>
      </header>

      <div className="px-6 pt-4">
        {isTrial && (
          <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="paywall-trial-note">
            <p className="font-heading text-sm font-bold text-emerald-900">Anda sedang masa coba gratis 3 hari 🎉</p>
            <p className="mt-1 text-xs leading-relaxed text-emerald-700">
              3 hari pertama gratis — nikmati semua fitur Premium sekarang. Berlangganan agar
              akses tetap aktif setelah masa coba berakhir.
            </p>
          </div>
        )}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="overflow-hidden rounded-3xl bg-zinc-900 p-6 text-white shield-shadow"
        >
          <div className="flex items-center gap-2 text-emerald-400">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-xs font-semibold uppercase tracking-wider">CeKKeU Premium</span>
          </div>
          <p className="mt-3 text-sm text-zinc-300">
            Pilih paket langganan. Makin panjang, makin hemat per bulan.
          </p>

          <div className="mt-4 space-y-2.5" data-testid="plan-selector">
            {PLANS.map((p) => {
              const active = p.id === planId;
              return (
                <button
                  key={p.id}
                  data-testid={`plan-${p.id}`}
                  onClick={() => setPlanId(p.id)}
                  className={`relative flex w-full items-center justify-between rounded-2xl border p-4 text-left transition-colors ${
                    active
                      ? "border-emerald-400 bg-emerald-500/10"
                      : "border-zinc-700 bg-zinc-800/40 hover:border-zinc-500"
                  }`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-sm font-bold text-white">{p.label}</span>
                      {p.tagline && (
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                          active ? "bg-emerald-500 text-white" : "bg-zinc-700 text-emerald-300"
                        }`}>
                          {p.tagline}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-zinc-400">
                      {formatPrice(p.perMonth)}/bulan
                      {p.months > 1 ? ` · ditagih ${formatPrice(p.price)}` : ""}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="font-mono text-lg font-black tracking-tight text-white" data-testid={`plan-price-${p.id}`}>
                      {formatPrice(p.price)}
                    </span>
                    <div className={`ml-auto mt-1 flex h-5 w-5 items-center justify-center rounded-full border ${
                      active ? "border-emerald-400 bg-emerald-400" : "border-zinc-600"
                    }`}>
                      {active && <Check className="h-3 w-3 text-zinc-900" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-zinc-500">
            Akses penuh fitur premium selama masa langganan. Perpanjangan manual sebelum
            masa aktif berakhir agar premium tak terputus.
          </p>
        </motion.div>

        <div className="mt-5 rounded-3xl border border-zinc-200 bg-white p-6 shield-shadow">
          <p className="mb-4 font-heading text-xs font-semibold uppercase tracking-wider text-zinc-400">
            Yang Anda Dapatkan
          </p>
          <div className="space-y-3">
            {BENEFITS.map((b) => (
              <div key={b} className="flex items-center gap-3">
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100">
                  <Check className="h-3 w-3 text-emerald-700" />
                </div>
                <span className="text-sm text-zinc-700">{b}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Voucher */}
        <div className="mt-5 rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow">
          <div className="mb-2 flex items-center gap-2">
            <Ticket className="h-4 w-4 text-emerald-600" />
            <p className="font-heading text-sm font-bold text-zinc-900">Punya kode voucher?</p>
          </div>
          <p className="mb-3 text-xs text-zinc-500">Tukarkan kode promo untuk akses premium gratis 30 hari.</p>
          <div className="flex gap-2">
            <input
              data-testid="voucher-input"
              value={voucher}
              onChange={(e) => setVoucher(e.target.value.toUpperCase())}
              placeholder="mis. PROMO1BULAN"
              className="h-11 flex-1 rounded-xl border border-zinc-200 bg-white px-3 font-mono text-sm uppercase tracking-wide text-zinc-900 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            />
            <Button
              data-testid="voucher-redeem-btn"
              onClick={redeem}
              disabled={redeeming || !voucher.trim()}
              className="h-11 rounded-xl bg-emerald-600 px-4 text-sm font-semibold hover:bg-emerald-700"
            >
              {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pakai"}
            </Button>
          </div>
        </div>

        {/* Auto-renew (recurring card) toggle — disabled while only GoPay is
            active (card auto-charge not yet enabled on the Midtrans account). */}
        {false && (
        <div className="mt-5 rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow" data-testid="auto-renew-card">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              <div>
                <p className="font-heading text-sm font-bold text-zinc-900">Perpanjang otomatis tiap bulan</p>
                <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                  Kartu Anda ditagih otomatis tiap bulan agar premium tak terputus. Hanya
                  untuk kartu kredit/debit. Bisa dibatalkan kapan saja dari Profil.
                </p>
              </div>
            </div>
            <Switch
              data-testid="auto-renew-toggle"
              checked={autoRenew}
              onCheckedChange={setAutoRenew}
            />
          </div>
        </div>
        )}

        {/* Gratis vs Premium — apa yang didapat di tiap paket */}
        <div className="mt-6" data-testid="paywall-compare">
          <p className="mb-3 font-heading text-sm font-bold text-zinc-900">Gratis vs Premium</p>
          <CompareTable />
        </div>

        <p className="mt-4 px-1 text-center text-[11px] text-zinc-400">
          Pembayaran aman via Midtrans (GoPay). Akses premium tersimpan di akun Anda.
        </p>
      </div>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-1/2 w-full max-w-md lg:max-w-3xl -translate-x-1/2 border-t border-zinc-100 bg-white/95 p-4 backdrop-blur-lg">
        <Button
          data-testid="subscribe-cta"
          onClick={subscribe}
          disabled={paying}
          className="h-14 w-full rounded-full bg-zinc-900 text-base font-semibold hover:bg-zinc-800"
        >
          {paying ? (
            <span className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Memproses…</span>
          ) : autoRenew ? (
            `Aktifkan Auto-Perpanjang — ${formatPrice(pricing.price)}/bulan`
          ) : (
            `Berlangganan ${selectedPlan.label} — ${formatPrice(selectedPlan.price)}`
          )}
        </Button>
        {!isAuthenticated && (
          <p className="mt-2 text-center text-[11px] text-zinc-400">Perlu masuk/daftar untuk berlangganan.</p>
        )}
      </div>
    </div>
  );
}
